import { ArrowRight, Plus, Settings2, X } from 'lucide-react';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { PetalSwatch } from '@/components/brand/ServiceFan';
import { ServiceFanStage, type FanAsideApi } from '@/components/brand/ServiceFanStage';
import { Badge, Button, IconButton, Kbd, PageHeader, Switch, useToast } from '@/components/ui';
import { useObjectOpen } from '@/components/layout/workspace/useObjectOpen';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import {
  CATEGORY_LABEL,
  SAMPLE_FACTS,
  at,
  fanPlates,
  infraPath,
  kindLabel,
  myServices,
  servicesLabel,
  withServices,
  type MyService,
} from '../model';
import { usePrefs } from '../prefs';
import { Body, Page } from './common';
import { ModuleIcon, ModuleStatus } from './MarketplaceScreen';

/*
  Marketplace › «Мои сервисы» — веер сервисов (spec-4, R43): по пластине на каждый подключённый сервис,
  в порядке подключения, с 6 часов по часовой. Светлая — включён, контур — выключен, красная — сбой.
  Сцена тёмная в обеих темах; справа на ней неподвижная колонка: сводка в покое, карточка активной
  пластины. Без сервисов — «заклёпка без пластин» на том же месте, первая пластина придёт без сдвига.

  Включение и выключение — сразу (R39): выключение с «Отменить» в тосте и Ctrl+Z, включение подтверждает
  свет, налившийся в пластину. Подключённое в Marketplace, пока страницы не было, выходит пластиной после
  показа (fanSeen — своё «показано» у веера).
*/

/** Больше стольких новых за раз — уже не событие: без прихода и без строки «Новый». */
const NEWS_MAX = 6;

export function MyServicesScreen() {
  const { go } = useWorkspace();
  const { toast } = useToast();
  const prefs = usePrefs();
  const { services, fanSeen, set } = prefs;
  const list = myServices(prefs);
  const plates = fanPlates(prefs);
  const n = list.length;
  const on = list.filter((s) => s.on).length;
  const alerts = list.filter((s) => s.alert).length;
  const canConnect = withServices(services, prefs.servicesOff).some((m) => !m.installed && !m.soon);

  // Пришло без страницы — веер разберётся при показе сам (до шести — приходом, больше — сразу на месте).
  const [arriving] = useState(() => services.filter((id) => !fanSeen.includes(id)));

  // «Новый: GitLab» в сводке: держится до первого действия на странице.
  const key = services.join('|');
  const added = () => {
    const ids = services.filter((id) => !fanSeen.includes(id));
    return ids.length <= NEWS_MAX ? ids : [];
  };
  const [news, setNews] = useState(() => ({ key, ids: added() }));
  if (news.key !== key) setNews({ key, ids: added() });
  const quiet = useCallback(() => setNews((s) => (s.ids.length ? { ...s, ids: [] } : s)), []);

  // «Показано» у веера — когда веер показал состав на экране: приход уже запущен, повторно он не сыграет.
  // В скрытой вкладке пришедшие сюда не попадают — выйдут пластиной после показа.
  const seen = (ids: readonly string[]) => {
    if (ids.length !== fanSeen.length || ids.some((id, i) => id !== fanSeen[i])) set({ fanSeen: [...ids] });
  };

  const turnOn = (id: string) => set((p) => ({ servicesOff: p.servicesOff.filter((x) => x !== id) }));
  const toggle = (s: MyService, next: boolean) => {
    quiet();
    const id = s.module.id;
    if (next) return turnOn(id);
    set((p) => ({ servicesOff: p.servicesOff.includes(id) ? p.servicesOff : [...p.servicesOff, id] }));
    toast({
      title: `Сервис «${s.module.name}» выключен`,
      description: 'Настройки сохранены',
      action: { label: 'Отменить', undo: true, onClick: () => turnOn(id) },
    });
  };

  return (
    <Page>
      <PageHeader
        title="Мои сервисы"
        meta={
          n === 0 ? (
            <span>{servicesLabel(0)}</span>
          ) : (
            <>
              <span>{servicesLabel(n)}</span>
              <span>включено {on}</span>
              {alerts > 0 && <span>со сбоем {alerts}</span>}
            </>
          )
        }
        actions={
          n > 0 && canConnect ? (
            <Button
              variant="secondary"
              leftIcon={<Plus />}
              onClick={() => go(at('/marketplace/recommended'))}
            >
              Подключить сервис
            </Button>
          ) : undefined
        }
      />
      <Body>
        <ServiceFanStage
          plates={plates}
          label={`Мои сервисы: ${n}, включено ${on}`}
          arriving={arriving}
          onSeen={seen}
          aside={(api) => {
            const s = api.plate && list.find((x) => x.module.id === api.plate?.id);
            return s ? (
              <ServiceCard api={api} service={s} onToggle={toggle} onSeen={quiet} />
            ) : (
              <FanSummary api={api} list={list} news={news.ids} />
            );
          }}
          empty={
            <div className="flex h-full flex-col justify-center gap-3">
              {/* Неразрывные пробелы: тире не начинает строку, «ещё не веер» не рвётся. */}
              <p className="type-heading text-petal">{'Заклёпка без пластин\u00a0— ещё\u00a0не\u00a0веер'}</p>
              <p className="text-sm text-petal/70">
                Подключите сервис в Marketplace — он станет первой пластиной этого веера: включённый светлый,
                выключенный контуром.
              </p>
              <div>
                <Button onClick={() => go(at('/marketplace/recommended'))}>Открыть Marketplace</Button>
              </div>
            </div>
          }
        />
      </Body>
    </Page>
  );
}

/** Сводка в покое: легенда — она же счёт, сбои и новые с «Показать», подсказка с клавишами. */
function FanSummary({
  api,
  list,
  news,
}: {
  api: FanAsideApi;
  list: readonly MyService[];
  news: readonly string[];
}) {
  const on = list.filter((s) => s.on).length;
  const off = list.length - on;
  const alerts = list.filter((s) => s.alert);
  const fresh = news.flatMap((id) => list.find((s) => s.module.id === id) ?? []);
  return (
    <>
      <p className="type-heading text-fg">{servicesLabel(list.length)}</p>
      <div className="flex flex-col gap-1.5 text-sm text-fg-muted">
        <span className="flex items-center gap-2">
          <PetalSwatch on />
          включено {on}
        </span>
        {off > 0 && (
          <span className="flex items-center gap-2">
            <PetalSwatch on={false} />
            выключено {off}
          </span>
        )}
        {alerts.length > 0 && (
          <span className="flex items-center gap-2">
            <PetalSwatch on alert />
            со сбоем {alerts.length}
          </span>
        )}
      </div>
      {alerts.map((s) => (
        <div key={s.module.id} className="flex min-w-0 items-center gap-2 text-xs">
          <Badge variant="urgent" solid>
            Сбой
          </Badge>
          <span className="shrink-0 text-fg">{s.module.name}</span>
          <span className="min-w-0 truncate text-fg-muted">{s.alert}</span>
          <Button variant="link" size="sm" className="ml-auto shrink-0" onClick={() => api.show(s.module.id)}>
            Показать
          </Button>
        </div>
      ))}
      {fresh.length > 0 && (
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <span className="min-w-0 truncate text-fg">
            {fresh.length === 1 ? 'Новый' : 'Новые'}: {fresh.map((s) => s.module.name).join(', ')}
          </span>
          <Button
            variant="link"
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => api.show(fresh[0].module.id)}
          >
            Показать
          </Button>
        </div>
      )}
      <div className="mt-auto flex flex-col gap-1.5">
        {list.length > 36 && (
          <p className="type-caption text-fg-muted">
            Пластин больше 36 — они становятся уже. Позже сервисы можно будет собрать в группы по разделам
            Marketplace.
          </p>
        )}
        <p className="type-caption text-fg-muted">Наведите на пластину или пройдите стрелками</p>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 type-caption text-fg-muted">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Kbd>←</Kbd>
            <Kbd>→</Kbd>— по кругу
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Kbd>Enter</Kbd>— к действиям
          </span>
          <span className="whitespace-nowrap">буква — к сервису</span>
        </p>
      </div>
    </>
  );
}

/** Карточка активной пластины: место в порядке подключения, сведения и действия «открыть / настройки / вкл-выкл». */
function ServiceCard({
  api,
  service: s,
  onToggle,
  onSeen,
}: {
  api: FanAsideApi;
  service: MyService;
  onToggle: (s: MyService, on: boolean) => void;
  onSeen: () => void;
}) {
  const { go } = useWorkspace();
  const open = useObjectOpen();
  const { toast } = useToast();
  const m = s.module;
  const app = m.id === 'infra' && s.on;
  const facts = s.on ? SAMPLE_FACTS[m.id] : undefined;
  // Вид · категория; длинная категория рядом с меткой состояния обрезается — целиком в подсказке.
  const where = `${kindLabel(m)} · ${CATEGORY_LABEL[m.category] ?? m.category}`;

  // Карточка открылась — человек начал работать со страницей: «Новый» в сводке больше не нужен.
  useEffect(() => onSeen(), [onSeen]);

  const page = at(`/marketplace/m/${m.id}`);
  const openPage = (e: MouseEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.button !== 1) return;
    e.preventDefault();
    open(page, e);
  };

  return (
    <>
      <div className="flex h-8 items-center justify-between">
        <span className="type-overline text-fg-subtle">
          {api.index + 1} из {api.total}
        </span>
        {api.pinned && (
          <IconButton size="sm" variant="ghost" aria-label="К сводке" onClick={api.unpin}>
            <X />
          </IconButton>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-md border border-line bg-muted text-fg">
          <ModuleIcon id={m.id} className="size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate type-subheading text-fg">{m.name}</span>
          <span className="truncate text-2xs text-fg-subtle" title={where}>
            {where}
          </span>
        </div>
        <ModuleStatus module={m} alert={!!s.alert} />
      </div>
      {facts && <p className="text-xs text-fg-muted">{facts}</p>}
      {s.alert && <p className="text-xs text-danger-fg">{s.alert}</p>}
      <p className="line-clamp-2 text-xs text-fg-muted">{m.description}</p>
      <div className="mt-auto border-t border-line pt-3">
        {/* gap-1: «Страница сервиса», «Настройки» и переключатель занимают 282 из 286 px строки. */}
        <div className="flex h-8 items-center gap-1">
          {app ? (
            <Button
              size="sm"
              rightIcon={<ArrowRight />}
              data-card-primary=""
              onClick={() => go(at('/apps/infra'))}
            >
              Открыть
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              data-card-primary=""
              onClick={openPage}
              onAuxClick={openPage}
            >
              Страница сервиса
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Settings2 />}
            onClick={() =>
              app
                ? go(infraPath('manage-settings'))
                : toast({ title: `${m.name}: настройки откроются в приложении`, tone: 'success' })
            }
          >
            Настройки
          </Button>
          <Switch
            size="sm"
            className="ml-auto"
            aria-label="Включён"
            title="Выключенный сервис не шлёт событий; настройки сохраняются"
            checked={s.on}
            onCheckedChange={(next) => onToggle(s, next)}
          />
        </div>
      </div>
    </>
  );
}
